import type { Epic } from '@/features/project-board/types';

const DAY_MS = 86_400_000;

/** 달력에 놓는 일정 한 건 — 에픽 하나가 검증 기간 하나와 배포 목표일 하나를 가질 수 있다. */
export interface QaCalendarEvent {
  epic: Epic;
  kind: 'review' | 'release';
  /** `YYYY-MM-DD`. 배포 목표일은 시작과 끝이 같다. */
  start: string;
  end: string;
}

/** 한 주 안에 그려지는 일정 조각. 주를 넘는 일정은 주마다 조각이 하나씩 생긴다. */
export interface QaCalendarSegment {
  event: QaCalendarEvent;
  /** 그 주 안에서의 줄 번호(0부터). 같은 주 안에서 한 일정은 한 줄에 머문다. */
  lane: number;
  /** 시작 칸(0=월요일)과 칸 수. */
  column: number;
  span: number;
  /** 지난주에서 이어져 왔나 / 다음 주로 이어지나 — 막대 모서리를 그리는 데 쓴다. */
  continuesBefore: boolean;
  continuesAfter: boolean;
}

/**
 * QA 일정 달력 — 역기획서에 적힌 검증 기간·배포 목표일을 월 달력에 놓는다.
 *
 * 날짜는 `YYYY-MM-DD` 문자열 그대로 다룬다. 일정은 하루 단위 값이라 시각이 없고, 지역 시간대 `Date`로
 * 바꾸면 자정 언저리에서 하루가 밀린다 — 계산은 UTC 자정으로만 한다.
 *
 * 주는 월요일에 시작한다. QA는 평일 일이라 주말 두 칸을 끝에 몰아 흐리게 그린다.
 */
export class QaCalendar {
  /** 일정이 있는 에픽의 달력 항목. 검증 기간이 없는 에픽도 배포 목표일만 있으면 놓는다. */
  static eventsOf(epics: readonly Epic[]): QaCalendarEvent[] {
    return epics.flatMap((epic) => {
      const schedule = epic.qaSchedule;
      if (!schedule) return [];
      const events: QaCalendarEvent[] = [];
      if (schedule.reviewStart) {
        events.push({ epic, kind: 'review', start: schedule.reviewStart, end: schedule.reviewEnd ?? schedule.reviewStart });
      }
      if (schedule.releaseTarget) {
        events.push({ epic, kind: 'release', start: schedule.releaseTarget, end: schedule.releaseTarget });
      }
      return events;
    });
  }

  /** `2026-10-13` → `2026-10`. */
  static monthOf(date: string): string {
    return date.slice(0, 7);
  }

  /** `2026-10`에서 `delta`달 옮긴 달. */
  static shiftMonth(month: string, delta: number): string {
    const [year, monthNumber] = month.split('-').map(Number);
    const shifted = new Date(Date.UTC(year, monthNumber - 1 + delta, 1));
    return shifted.toISOString().slice(0, 7);
  }

  /** 그 달을 덮는 주들(월~일 7칸씩). 앞뒤 달의 날짜로 첫 주·마지막 주를 채운다. */
  static weeksOf(month: string): string[][] {
    const [year, monthNumber] = month.split('-').map(Number);
    const first = Date.UTC(year, monthNumber - 1, 1);
    const last = Date.UTC(year, monthNumber, 0);
    // getUTCDay: 일=0 … 토=6 → 월=0 … 일=6
    const start = first - ((new Date(first).getUTCDay() + 6) % 7) * DAY_MS;
    const weeks: string[][] = [];
    for (let weekStart = start; weekStart <= last; weekStart += 7 * DAY_MS) {
      weeks.push(Array.from({ length: 7 }, (_, offset) => QaCalendar.isoOf(weekStart + offset * DAY_MS)));
    }
    return weeks;
  }

  /**
   * 한 주에 걸치는 일정 조각과 그 줄 번호.
   *
   * 먼저 시작한 것·긴 것부터 위 줄에 앉힌다 — 겹치지 않는 가장 위 줄을 고른다. 같은 주 안에서는 한 일정이
   * 한 줄에 이어져 보여야 막대로 읽힌다.
   */
  static segmentsOf(week: readonly string[], events: readonly QaCalendarEvent[]): QaCalendarSegment[] {
    const weekStart = week[0];
    const weekEnd = week[week.length - 1];
    const overlapping = events
      .filter((event) => event.start <= weekEnd && event.end >= weekStart)
      .sort((left, right) =>
        left.start !== right.start
          ? left.start.localeCompare(right.start)
          : right.end.localeCompare(left.end) || left.kind.localeCompare(right.kind),
      );

    const laneEnds: number[] = [];
    return overlapping.map((event) => {
      const column = event.start < weekStart ? 0 : week.indexOf(event.start);
      const lastColumn = event.end > weekEnd ? week.length - 1 : week.indexOf(event.end);
      let lane = laneEnds.findIndex((end) => end < column);
      if (lane === -1) lane = laneEnds.length;
      laneEnds[lane] = lastColumn;
      return {
        event,
        lane,
        column,
        span: lastColumn - column + 1,
        continuesBefore: event.start < weekStart,
        continuesAfter: event.end > weekEnd,
      };
    });
  }

  /** 에픽마다 고정된 색 번호. 달이 바뀌어도 같은 에픽은 같은 색이라 막대를 따라 읽을 수 있다. */
  static toneOf(epic: Pick<Epic, 'source' | 'slug'>, toneCount: number): number {
    let hash = 0;
    for (const char of `${epic.source}#${epic.slug}`) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
    return hash % toneCount;
  }

  static isoOf(epochMs: number): string {
    return new Date(epochMs).toISOString().slice(0, 10);
  }

  /** 오늘(한국 시간)의 `YYYY-MM-DD`. 보드는 한국 팀이 쓴다. */
  static today(now: Date = new Date()): string {
    return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(now);
  }
}
